import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from '@phosphor-icons/react';

/**
 * Modal de confirmación reusable.
 * Diseño: card blanca con X arriba a la derecha, título bold, mensaje, y dos
 * botones pill (cancel claro / confirm sólido en cta-bg).
 *
 * Props:
 *   - isOpen, onClose, onConfirm
 *   - title (string)
 *   - message (string o ReactNode)
 *   - confirmLabel (default "Sí")
 *   - cancelLabel  (default "No")
 */
export default function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = 'Sí',
  cancelLabel = 'No',
  zIndex = 60,
}) {
  useEffect(() => {
    if (!isOpen) return;
    const html = document.documentElement;
    const body = document.body;
    const ho = html.style.overflow;
    const bo = body.style.overflow;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    return () => {
      html.style.overflow = ho;
      body.style.overflow = bo;
    };
  }, [isOpen]);

  // Portal a document.body para escapar cualquier contexto de `transform`
  // del ancestro (ej. HomeCartSummary tiene `translate-y-*` lo que rompe
  // position:fixed de descendientes y empuja el modal al fondo del cart bar).
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50"
            style={{ zIndex }}
          />
          <div className="fixed inset-0 flex items-center justify-center px-6 pointer-events-none" style={{ zIndex }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-3xl shadow-2xl w-full max-w-xs p-5 pointer-events-auto relative"
            >
              <button
                type="button"
                onClick={onClose}
                className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center text-gray-500 active:scale-90 transition-transform"
                aria-label="Cerrar"
              >
                <X size={20} weight="bold" />
              </button>

              <h2 className="text-xl font-extrabold font-display tracking-tight text-gray-900 mb-1.5 pr-7">
                {title}
              </h2>
              <p className="text-sm text-gray-700 leading-snug mb-5">
                {message}
              </p>

              <div className="flex gap-2.5">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-2.5 rounded-full bg-primary-100 text-cta-bg font-bold text-sm active:scale-[0.98] transition-transform"
                >
                  {cancelLabel}
                </button>
                <button
                  type="button"
                  onClick={onConfirm}
                  className="flex-1 py-2.5 rounded-full bg-cta-bg hover:bg-cta-hover text-white font-bold text-sm active:scale-[0.98] transition-transform"
                >
                  {confirmLabel}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}
